import { useFeatureFlags } from '../hooks/useFeatureFlags';
import logger from '@/utils/logger';

/**
 * Composant pour protéger l'accès aux fonctionnalités selon l'abonnement
 */
export const FeatureGate = ({
  page,
  feature,
  tab,
  action,
  fallback = null,
  upgradePrompt = false,
  upgradeMessage,
  requiredSubscription,
  children,
  className = '',
}) => {
  const { isFeatureEnabled, isTabEnabled, canPerformAction, getRequiredSubscription, currentSubscription, isLoaded } =
    useFeatureFlags();

  // Attendre le chargement de la configuration
  if (!isLoaded) {
    return <div className="loading-placeholder">Chargement...</div>;
  }

  let isEnabled = true;
  let required = requiredSubscription;

  // Vérifier selon le type de protection
  if (feature) {
    isEnabled = isFeatureEnabled(page, feature);
    if (!required) required = getRequiredSubscription(page, feature);
  } else if (tab) {
    isEnabled = isTabEnabled(page, tab);
    if (!required) required = getRequiredSubscription(page, `tabs.${tab}`);
  } else if (action) {
    isEnabled = canPerformAction(page, action);
    if (!required) required = getRequiredSubscription(page, action);
  }

  // Si la fonctionnalité est activée, afficher le contenu
  if (isEnabled) {
    return <div className={className}>{children}</div>;
  }

  // Si désactivée et qu'on veut un prompt d'upgrade
  if (upgradePrompt) {
    return (
      <UpgradePrompt
        feature={feature || tab || action}
        page={page}
        currentSubscription={currentSubscription}
        requiredSubscription={required}
        message={upgradeMessage}
        className={className}
      />
    );
  }

  // Sinon, afficher le fallback ou rien
  return fallback;
};

/**
 * Composant d'upgrade prompt
 */
const UpgradePrompt = ({ currentSubscription, requiredSubscription, message, className = '' }) => {
  const getSubscriptionColor = (sub) => {
    const colors = {
      basic: 'bg-gray-100 text-gray-800',
      premium: 'bg-blue-100 text-blue-800',
      flex: 'bg-purple-100 text-purple-800',
    };
    return colors[sub] || colors.basic;
  };

  const getSubscriptionIcon = (sub) => {
    const icons = {
      basic: '🥉',
      premium: '🥈',
      flex: '🥇',
    };
    return icons[sub] || '⭐';
  };

  const defaultMessage =
    message || `Cette fonctionnalité nécessite l'abonnement ${requiredSubscription?.toUpperCase()}.`;

  return (
    <div
      className={`upgrade-prompt border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50 ${className}`}
    >
      <div className="mb-4">
        <div className="text-4xl mb-2">{getSubscriptionIcon(requiredSubscription)}</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Fonctionnalité {requiredSubscription?.toUpperCase()}
        </h3>
      </div>

      <p className="text-gray-600 mb-4">{defaultMessage}</p>

      <div className="flex items-center justify-center gap-2 mb-4">
        <span className="text-sm text-gray-500">Actuel:</span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSubscriptionColor(currentSubscription)}`}>
          {currentSubscription?.toUpperCase()}
        </span>
        <span className="text-gray-400">→</span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSubscriptionColor(requiredSubscription)}`}>
          {requiredSubscription?.toUpperCase()}
        </span>
      </div>

      <div className="space-y-2">
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          onClick={() => handleUpgrade(requiredSubscription)}
        >
          Passer au {requiredSubscription?.toUpperCase()}
        </button>

        <div>
          <button
            className="text-blue-600 hover:text-blue-800 text-sm underline"
            onClick={() => showFeatureComparison()}
          >
            Comparer les abonnements
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Composant pour masquer complètement une fonctionnalité
 */
export const FeatureHide = ({ page, feature, tab, action, children }) => {
  return (
    <FeatureGate page={page} feature={feature} tab={tab} action={action} fallback={null}>
      {children}
    </FeatureGate>
  );
};

/**
 * Composant pour désactiver une fonctionnalité avec message
 */
export const FeatureDisable = ({
  page,
  feature,
  tab,
  action,
  children,
  disabledMessage = 'Fonctionnalité non disponible avec votre abonnement',
}) => {
  const { isFeatureEnabled, isTabEnabled, canPerformAction, isLoaded } = useFeatureFlags();

  if (!isLoaded) return children;

  let isEnabled = true;

  if (feature) {
    isEnabled = isFeatureEnabled(page, feature);
  } else if (tab) {
    isEnabled = isTabEnabled(page, tab);
  } else if (action) {
    isEnabled = canPerformAction(page, action);
  }

  if (!isEnabled) {
    return (
      <div className="relative">
        <div className="opacity-50 pointer-events-none">{children}</div>
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 rounded">
          <span className="text-white text-sm font-medium px-3 py-1 bg-gray-800 rounded">{disabledMessage}</span>
        </div>
      </div>
    );
  }

  return children;
};

/**
 * Composant pour les badges d'abonnement
 */
export const SubscriptionBadge = ({ subscription, size = 'sm' }) => {
  const getSubscriptionStyle = (sub) => {
    const styles = {
      basic: 'bg-gray-100 text-gray-800 border-gray-300',
      premium: 'bg-blue-100 text-blue-800 border-blue-300',
      flex: 'bg-purple-100 text-purple-800 border-purple-300',
    };
    return styles[sub] || styles.basic;
  };

  const sizeClasses = {
    xs: 'px-1 py-0.5 text-xs',
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  return (
    <span
      className={`
      inline-flex items-center rounded-full border font-medium
      ${getSubscriptionStyle(subscription)}
      ${sizeClasses[size]}
    `}
    >
      {subscription?.toUpperCase()}
    </span>
  );
};

// Fonctions utilitaires
const handleUpgrade = (targetSubscription) => {
  // Rediriger vers la page d'upgrade ou ouvrir un modal
  logger.debug(`Upgrade vers ${targetSubscription}`);
  // Implémentation selon votre système de paiement
};

const showFeatureComparison = () => {
  // Ouvrir un modal de comparaison des fonctionnalités
  logger.debug('Afficher la comparaison des abonnements');
};

export default FeatureGate;
